import React, {useRef, useState, useEffect, useCallback} from "react";
import {Box, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import CustomCourseCard from "./CustomCourseCard";
import NavigationArrow from "./NavigationArrow";

interface FeaturedImage {
	ext: string;
	url: string;
	hash: string;
	mime: string;
	name: string;
	path: string | null;
	size: number;
	width: number;
	height: number;
}

export interface CustomLibraryCardData {
	title: string;
	description?: string;
	contentType?: string;
	userEnrollmentType?: string;
	authorCohort?: {
		title: string;
	};
	slug?: string;
	percentCompleted?: number;
	localizations?: Array<{
		title?: string;
		description?: string;
	}>;
	featuredImage?: FeaturedImage;
	lessonDuration: number; // in minutes
	status?: string;
	userProgressPercentage?: number;
	totalDurationSeconds?: number;
	watchedDurationSeconds?: number;
	remainingDurationSeconds?: number;
	courseProgress?: number;
}

interface CustomLibraryCarouselsProps {
	contentType: string;
	data: CustomLibraryCardData[];
	className?: string;
	onLoadMore?: (category: string) => void;
	hasMoreData?: boolean;
	isLoading?: boolean;
}

const CARD_HEIGHT = 380;

const useStyles = makeStyles()((theme) => ({
	container: {
		width: "100%",
		marginBottom: theme.customSpacing.carouselGap
	},
	title: {
		marginBottom: theme.spacing(3),
		marginLeft: theme.spacing(2),
		fontWeight: 700
	},
	carouselContainer: {
		position: "relative",
		width: "100%"
	},
	scrollContainer: {
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		gap: "10px",
		paddingBottom: theme.spacing(2),
		scrollbarWidth: "none", // Firefox
		"&::-webkit-scrollbar": {
			display: "none" // Chrome, Safari, Edge
		},
		cursor: "grab",
		"&:active": {
			cursor: "grabbing"
		},
		userSelect: "none"
	}
}));

export const CustomLibraryCarousels: React.FC<CustomLibraryCarouselsProps> = ({
	contentType,
	data,
	className,
	onLoadMore,
	hasMoreData = false,
	isLoading = false
}) => {
	const classes = useStyles();
	const containerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [cardWidth, setCardWidth] = useState(300);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Adding new refs to avoid stale closures in scroll handlers
	// Reference these with someName.current to get the latest value regardless of embedding in functions.
	const isLoadingMoreRef = useRef(false);
	const isLoadingRef = useRef(isLoading);
	const hasMoreDataRef = useRef(hasMoreData);
	const lastLoadMoreTimeRef = useRef<number>(0);

	// Keep refs in sync with existing props/state synchronously during render
	// This ensures scroll events/timeouts always see the latest values, avoiding race conditions
	isLoadingRef.current = isLoading;
	hasMoreDataRef.current = hasMoreData;

	const loadMoreData = useCallback(() => {
		// Prevent rapid-fire calls - ensure at least 500ms between load more requests (This could be removed in the future if it's causing lag)
		const now = Date.now();
		if (now - lastLoadMoreTimeRef.current < 500) {
			return;
		}

		if (onLoadMore && !isLoadingMoreRef.current && !isLoadingRef.current && hasMoreDataRef.current) {
			lastLoadMoreTimeRef.current = now;
			isLoadingMoreRef.current = true;
			setIsLoadingMore(true);
			onLoadMore(contentType);

			// Clear any existing timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// Auto-reset after 2 seconds as fallback
			timeoutRef.current = setTimeout(() => {
				isLoadingMoreRef.current = false;
				setIsLoadingMore(false);
				timeoutRef.current = null;
			}, 2000);
		}
	}, [onLoadMore, contentType]);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const updateArrows = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;

		const hasScrollLeft = el.scrollLeft > 0;
		const hasScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 5;

		setCanScrollLeft(hasScrollLeft);
		setCanScrollRight(hasScrollRight);

		const isNearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 50;
		if (isNearEnd && onLoadMore && !isLoadingMoreRef.current && !isLoadingRef.current && hasMoreDataRef.current) {
			loadMoreData();
		}
	}, [onLoadMore, loadMoreData, contentType]);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const calculateCardWidth = () => {
		const el = containerRef.current;
		if (!el || el.children.length === 0) return;

		const firstCard = el.children[0] as HTMLElement;
		const cardRect = firstCard.getBoundingClientRect();

		// Calculate the total width including margins and gaps
		const cardWidthWithSpacing = cardRect.width +
			parseInt(window.getComputedStyle(firstCard).marginLeft) +
			parseInt(window.getComputedStyle(firstCard).marginRight) +
			parseInt(window.getComputedStyle(el).gap || "16");

		setCardWidth(cardWidthWithSpacing);
	};

	// Reset isLoadingMore when data arrives or search completes (for UI)
	useEffect(() => {
		if (isLoadingMoreRef.current && (!isLoading || !hasMoreData)) {
			// Clear timeout if data loaded before timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			isLoadingMoreRef.current = false;
			setIsLoadingMore(false);
		}
	}, [isLoading, hasMoreData, contentType]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		updateArrows();
		calculateCardWidth();

		const el = containerRef.current;
		if (!el) return;

		const handleResize = () => {
			updateArrows();
			calculateCardWidth();
		};

		el.addEventListener("scroll", updateArrows);
		window.addEventListener("resize", handleResize);

		return () => {
			el.removeEventListener("scroll", updateArrows);
			window.removeEventListener("resize", handleResize);
		};
	}, [updateArrows, data.length]);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handlePrev = (event: React.MouseEvent) => {
		event.stopPropagation();
		const el = containerRef.current;
		if (!el) return;
		el.scrollBy({left: -cardWidth, behavior: "smooth"});
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleNext = (event: React.MouseEvent) => {
		event.stopPropagation();
		const el = containerRef.current;
		if (!el) return;
		el.scrollBy({left: cardWidth, behavior: "smooth"});

		setTimeout(() => {
			if (!el) return;
			const isNearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
			if (isNearEnd && onLoadMore && !isLoadingMoreRef.current && !isLoadingRef.current && hasMoreDataRef.current) {
				loadMoreData();
			}
		}, 300); // Wait for scroll animation to complete
	};

	// Arrow vertical position: slightly below center of the card height
	const arrowTop = `${Math.round(CARD_HEIGHT / 2.5)}px`;

	// Drag and drop handlers
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleMouseDown = (e: React.MouseEvent) => {
		setIsDragging(true);
		setStartX(e.pageX - (containerRef.current?.offsetLeft || 0));
		setScrollLeft(containerRef.current?.scrollLeft || 0);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleMouseLeave = () => {
		setIsDragging(false);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleMouseUp = () => {
		setIsDragging(false);
	};

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging) return;
		e.preventDefault();
		const x = e.pageX - (containerRef.current?.offsetLeft || 0);
		const walk = (x - startX) * 2; // Scroll speed multiplier
		if (containerRef.current) {
			containerRef.current.scrollLeft = scrollLeft - walk;
		}
	};

	return (
		<Box className={`${classes.classes.container} ${className || ""}`}>
			<Typography variant="h2" className={classes.classes.title}>
				{contentType}
			</Typography>
			<Box className={classes.classes.carouselContainer}>
				<NavigationArrow
					direction="left"
					onClick={handlePrev}
					top={arrowTop}
					contentType={contentType}
					isLoading={false}
					disabled={!canScrollLeft || isLoading}
				/>
				<Box
					ref={containerRef}
					className={classes.classes.scrollContainer}
					onMouseDown={handleMouseDown}
					onMouseLeave={handleMouseLeave}
					onMouseUp={handleMouseUp}
					onMouseMove={handleMouseMove}
				>
					{data.map((item, idx) => (
						<CustomCourseCard
							key={item.title + idx}
							title={item.title}
							contentType={item.contentType}
							userEnrollmentType={item.userEnrollmentType}
							authorCohort={item.authorCohort}
							localizations={item.localizations}
							medium={item.featuredImage?.url || "/course_image.png"}
							slug={item.slug}
							index={idx + 1}
							percentCompleted={item.percentCompleted}
							minutes={item.lessonDuration}
							userProgressPercentage={item.userProgressPercentage}
							totalDurationSeconds={item.totalDurationSeconds}
							watchedDurationSeconds={item.watchedDurationSeconds}
							remainingDurationSeconds={item.remainingDurationSeconds}
							courseProgress={item.courseProgress}
						/>
					))}
				</Box>
				<NavigationArrow
					direction="right"
					onClick={handleNext}
					top={arrowTop}
					contentType={contentType}
					isLoading={isLoadingMore && !isLoading}
					disabled={isLoading || !canScrollRight}
				/>
			</Box>
		</Box>
	);
};

export default CustomLibraryCarousels;